import { Component, Fragment } from 'react'
import classNames from 'classnames'
import DOMPurify from 'dompurify'

import debounce from 'garden/src/js/utils/debounce'
import throttle from 'garden/src/js/utils/throttle'

import * as quicksearchRepository from 'scripts/api/header/quicksearch'

import 'mdn-polyfills/Element.prototype.closest'

import {
  TermList,
  ContentList,
  Form,
  StoreList,
  StoreItemDetail,
  ProductList,
  FidelityProgram
} from './components'

const DEFAULT_DEBOUNCE_TIMER = 400

const MEDIA_QUERY = 760
const ELEMENT_OFFSET = 200

class QuickSearch extends Component {
  DEBOUNCE_TIMER =
    parseInt(this.props.debounceTimer, 10) || DEFAULT_DEBOUNCE_TIMER

  constructor (props) {
    super(props)

    this.state = {
      isResultVisible: false,
      hasResults: false,
      products: [],
      terms: [],
      contents: [],
      stores: [],
      loyalty: {},
      currentTerm: null,
      isFixed: false
    }

    this.showResultsBox = this.showResultsBox.bind(this)
    this.hideResultsBox = this.hideResultsBox.bind(this)
    this.onFetchResultsSuccess = this.onFetchResultsSuccess.bind(this)
    this.checkResultsBoxState = this.checkResultsBoxState.bind(this)
    this.onInputChangeHandler = this.onInputChangeHandler.bind(this)
    this.renderTermList = this.renderTermList.bind(this)
    this.renderStoreList = this.renderStoreList.bind(this)
    this.renderProductList = this.renderProductList.bind(this)
    this.renderContentList = this.renderContentList.bind(this)
    this.renderFidelityProgram = this.renderFidelityProgram.bind(this)

    document.body.addEventListener('click', this.checkResultsBoxState)
  }

  componentDidMount () {
    this.initialPositionOnPage = document.documentElement.scrollTop
    this.previousPositionOnPage = this.initialPositionOnPage
    this.setViewport()

    window.addEventListener('resize', throttle(this.setViewport, 500))
    window.addEventListener('scroll', throttle(this.onScroll, 200))
  }

  checkResultsBoxState ({ target }) {
    const { isResultVisible, hasResults } = this.state

    if (target.closest('.quick-search') && hasResults) {
      return this.showResultsBox()
    }

    if (isResultVisible) {
      return this.hideResultsBox()
    }
  }

  onInputChangeHandler ({ currentTarget }) {
    this.fetchResults(DOMPurify.sanitize(currentTarget.value))
  }

  fetchResults = debounce(term => {
    const encodedTerm = encodeURIComponent(term)

    if (!encodedTerm) {
      return this.hideResultsBox() || this.clearResultsBox()
    }

    this.setState({ currentTerm: encodedTerm })
    this.getTerms(encodedTerm)
  }, this.DEBOUNCE_TIMER)

  getTerms = async term => {
    try {
      const terms = await quicksearchRepository.getTerms(term)

      if (terms.status !== 200) {
        return this.onFetchResultsFailure(terms.status)
      }

      this.onFetchResultsSuccess(terms.data)
    } catch (err) {
      this.onFetchResultsFailure(err)
    }
  }

  onFetchResultsSuccess (data) {
    const { terms, contents, products, stores, loyalty } = data
    const { termsCount, contentsCount, productsCount, storesCount } = data
    const hasResults =
      termsCount || contentsCount || productsCount || storesCount || Object.keys(loyalty).length

    this.updateTerms(terms)
    this.updateContents(contents)
    this.updateStores(stores)
    this.updateProducts(products)
    this.updateLoyalty(loyalty)

    this.setState({ hasResults })

    if (hasResults) {
      this.showResultsBox()
    }
  }

  onFetchResultsFailure (status) {
    this.hideResultsBox()
    this.clearResultsBox()

    throw new Error(`Cannot get terms, API returns status: ${status}`)
  }

  showResultsBox () {
    this.setState({ isResultVisible: true })
  }

  hideResultsBox () {
    this.setState({ isResultVisible: false })
  }

  clearResultsBox () {
    this.updateTerms()
    this.updateContents()
    this.updateStores()
    this.updateProducts()
    this.updateLoyalty()

    this.setState({ hasResults: false })
  }

  updateTerms (terms = []) {
    this.setState({ terms })
  }

  updateContents (contents = []) {
    this.setState({ contents })
  }

  updateStores (stores = []) {
    this.setState({ stores })
  }

  updateProducts (products = []) {
    this.setState({ products })
  }

  updateLoyalty (loyalty = {}) {
    this.setState({ loyalty })
  }

  renderTermList () {
    const { terms, currentTerm } = this.state
    const props = { terms, currentTerm }

    return <TermList {...props} />
  }

  renderContentList () {
    const { contents, currentTerm } = this.state
    const props = { contents, currentTerm }
    return <ContentList {...props} />
  }

  renderStoreList () {
    const { stores, currentTerm } = this.state
    const props = { stores, currentTerm }

    if (stores.length === 1) {
      return <StoreItemDetail {...stores[0]} currentTerm={currentTerm} />
    }

    return <StoreList {...props} />
  }

  renderProductList () {
    const { products, currentTerm } = this.state
    const props = { products, currentTerm }
    return <ProductList {...props} />
  }

  renderFidelityProgram () {
    const { loyalty } = this.state

    return <FidelityProgram {...loyalty} />
  }

  getRenderingFunction (type) {
    switch (type) {
      case 'term':
        return this.renderTermList
      case 'content':
        return this.renderContentList
      case 'store':
        return this.renderStoreList
      case 'product':
        return this.renderProductList
    }
  }

  getDirectionalLayout (componentBefore, componentAfter, isVertical) {
    const renderingFunctionBefore = this.getRenderingFunction(componentBefore)
    const renderingFunctionAfter = this.getRenderingFunction(componentAfter)
    const classesBefore = classNames({
      'col-xs-12': true,
      'no-padding': isVertical,
      'col-md-6': !isVertical,
      'border-right': !isVertical,
      'padding-x-small': !isVertical
    })
    const classesAfter = classNames({
      'col-xs-12': true,
      'col-md-6': !isVertical,
      'hidden-medium': isVertical
    })

    return (
      <Fragment>
        <div className={classesBefore}>{renderingFunctionBefore()}</div>
        <div className={classesAfter}>{renderingFunctionAfter()}</div>
      </Fragment>
    )
  }

  getDynamicLayout () {
    const { terms, contents, stores, products, loyalty } = this.state
    const termsCount = terms.length
    const productsCount = products.length
    const storesCount = stores.length
    const contentsCount = contents.length
    const isLoyalty = Object.keys(loyalty).length

    let isVertical, componentAfter

    if (isLoyalty) {
      return this.renderFidelityProgram()
    }

    if (termsCount && contentsCount && productsCount) {
      isVertical = true

      return (
        <Fragment>
          <div className="col-xs-12 col-md-6 border-right padding-x-small">
            {this.getDirectionalLayout('term', 'content', isVertical)}
          </div>
          <div className="col-xs-12 col-md-6 padding-x-small hidden-medium">
            {this.renderProductList()}
          </div>
        </Fragment>
      )
    }

    if (termsCount && (contentsCount || productsCount)) {
      componentAfter = contentsCount ? 'content' : 'product'
      isVertical = false

      return this.getDirectionalLayout('term', componentAfter, isVertical)
    }

    if (storesCount) {
      return this.renderStoreList()
    }

    const searchTerm = {
      term: `<b>${decodeURI(this.state.currentTerm)}</b>`,
      url: `/search?term=${this.state.currentTerm}`
    }

    return (
      <div className="col-xs-12">
        <TermList terms={[searchTerm]} />
        <div className="col-xs-12 no-padding">{this.renderContentList()}</div>
      </div>
    )
  }

  getViewport () {
    return document.documentElement.clientWidth
  }

  setViewport = () => {
    this.viewportPage = this.getViewport()
  }

  onScroll = () => {
    if (this.viewportPage <= MEDIA_QUERY) {
      this.currentPosition = document.documentElement.scrollTop

      this.pinSearchBar()
      this.unpinSearchBar()
    }
  }

  pinSearchBar () {
    this.setState({
      isFixed: this.previousPositionOnPage > this.currentPosition
    })

    this.previousPositionOnPage = this.currentPosition
  }

  unpinSearchBar () {
    if (this.currentPosition < ELEMENT_OFFSET) {
      this.setState({ isFixed: false })
    }
  }

  render () {
    const quickSearchClassName = classNames({
      'quick-search': true,
      fixed: this.state.isFixed
    })

    const resultsClassName = classNames({
      'results-box': true,
      active: this.state.isResultVisible
    })

    return (
      <div className={quickSearchClassName}>
        <Form onInputChange={this.onInputChangeHandler} />

        <div className={resultsClassName}>{this.getDynamicLayout()}</div>
      </div>
    )
  }
}

export default QuickSearch
